import { useMemo, useState } from "react";
import { styles } from "../styles";
import { hasCapability } from "../utils/capabilities";
import BackButton from "../components/BackButton";
import {
  groupByKey,
  jobPnl,
  monthKey,
  quarterKey,
  yearKey,
} from "../utils/margin";

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
const pct = (n) =>
  `${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

// Group-by options for the report. Each maps to a key extractor that
// reads from the jobPnl row.
const GROUP_BY = [
  { value: "customer", label: "Customer" },
  { value: "job_type", label: "Job Type" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

// Phase E: Margin reporting page. Aggregates per-job P&L from
// jobs.costing + jobs.bills (the math lives in src/utils/margin.js so
// every report uses identical logic). Group-by selector lets owners
// see profitability sliced by customer / job type / month / quarter /
// year. Sortable table with the standard ops columns: revenue, cost,
// margin $, margin %, markup %, job count.
//
// Gated by viewMargin capability — default-on in Owner + Billing dept
// presets.
export default function MarginReportPage({
  jobsRaw = [],
  customers = [],
  role,
  capabilities,
  onBack,
}) {
  const canView = hasCapability(role, capabilities, "viewMargin");

  const [groupBy, setGroupBy] = useState("customer");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("margin"); // margin | revenue | marginPct | jobs
  const [sortDir, setSortDir] = useState("desc"); // desc | asc

  const customerNameById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(c.id, c.name || "(unnamed)");
    return m;
  }, [customers]);

  // Per-job P&L for every job that has at least one non-voided bill OR
  // is legacy-finalized. T&M jobs without bills aren't billable yet so
  // we skip them — they'd skew the report with $0 revenue rows.
  const pnlRows = useMemo(() => {
    const rows = [];
    for (const job of jobsRaw) {
      const bills = (job.bills || []).filter((b) => !b.voided);
      if (bills.length === 0 && !job.billFinalized) continue;
      const row = jobPnl(job);
      // Date-range filter on finalizedAt (the row's "happened" date).
      if (dateFrom && (row.finalizedAt || "") < dateFrom) continue;
      if (
        dateTo &&
        (row.finalizedAt || "") > `${dateTo}T23:59:59.999Z`
      )
        continue;
      rows.push(row);
    }
    return rows;
  }, [jobsRaw, dateFrom, dateTo]);

  const grouped = useMemo(() => {
    let keyFn;
    let labelFn;
    if (groupBy === "customer") {
      keyFn = (r) => r.customerId || "";
      labelFn = (key) => customerNameById.get(key) || "(unknown customer)";
    } else if (groupBy === "job_type") {
      keyFn = (r) => r.jobType || "(none)";
      labelFn = (key) => key;
    } else if (groupBy === "month") {
      keyFn = monthKey;
      labelFn = (key) => key;
    } else if (groupBy === "quarter") {
      keyFn = quarterKey;
      labelFn = (key) => key;
    } else if (groupBy === "year") {
      keyFn = yearKey;
      labelFn = (key) => key;
    }
    const g = groupByKey(pnlRows, keyFn, labelFn);
    const dir = sortDir === "asc" ? 1 : -1;
    return [...g].sort((a, b) => {
      let av;
      let bv;
      if (sortBy === "jobs") {
        av = a.jobs;
        bv = b.jobs;
      } else if (sortBy === "marginPct") {
        av = a.marginPct;
        bv = b.marginPct;
      } else if (sortBy === "revenue") {
        av = a.revenue;
        bv = b.revenue;
      } else {
        av = a.margin;
        bv = b.margin;
      }
      if (av === bv) return 0;
      return av < bv ? -dir : dir;
    });
  }, [pnlRows, groupBy, customerNameById, sortBy, sortDir]);

  const totalRevenue = pnlRows.reduce((s, r) => s + r.revenue, 0);
  const totalCost = pnlRows.reduce((s, r) => s + r.totalCost, 0);
  const totalMargin = totalRevenue - totalCost;
  const totalMarginPct = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

  if (!canView) {
    return (
      <>
        <BackButton onClick={onBack} />
        <h2>Margin Report</h2>
        <p style={{ color: "#888" }}>
          You don't have permission to view margin reports.
        </p>
      </>
    );
  }

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  };
  const sortArrow = (col) =>
    sortBy === col ? (sortDir === "desc" ? " ▾" : " ▴") : "";

  const marginColor = (m) =>
    m > 0.005 ? "#1a7f37" : m < -0.005 ? "#b91c1c" : "#6b7280";

  return (
    <>
      <BackButton onClick={onBack} />
      <h2 style={{ margin: "8px 0 4px" }}>Margin Report</h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
        Realized P&amp;L from non-voided bills. Costs from costing rows
        (labor base × hrs, materials qty × unit cost, expenses + fuel).
        Tax excluded from revenue (pass-through).
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <label style={{ fontSize: "13px" }}>Group by:</label>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          {GROUP_BY.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label style={{ fontSize: "13px" }}>From:</label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <label style={{ fontSize: "13px" }}>To:</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>

      {/* Org totals across the filtered date range. */}
      <div
        style={{
          padding: "10px 12px",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          marginBottom: "12px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "8px",
        }}
      >
        <Cell label="Jobs in range" value={pnlRows.length} />
        <Cell label="Total revenue" value={money(totalRevenue)} />
        <Cell label="Total cost" value={money(totalCost)} />
        <Cell
          label="Gross profit"
          value={money(totalMargin)}
          color={marginColor(totalMargin)}
        />
        <Cell
          label="Margin %"
          value={pct(totalMarginPct)}
          color={marginColor(totalMargin)}
        />
      </div>

      {pnlRows.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>
          No billable jobs in this date range.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              fontSize: "13px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr
                style={{
                  color: "#374151",
                  textAlign: "left",
                  borderBottom: "1px solid #d1d5db",
                  background: "#f3f4f6",
                }}
              >
                <th style={{ padding: "6px 8px" }}>
                  {GROUP_BY.find((g) => g.value === groupBy)?.label}
                </th>
                <th
                  style={{
                    padding: "6px 8px",
                    textAlign: "right",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSort("jobs")}
                >
                  Jobs{sortArrow("jobs")}
                </th>
                <th
                  style={{
                    padding: "6px 8px",
                    textAlign: "right",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSort("revenue")}
                >
                  Revenue{sortArrow("revenue")}
                </th>
                <th
                  style={{ padding: "6px 8px", textAlign: "right" }}
                >
                  Cost
                </th>
                <th
                  style={{
                    padding: "6px 8px",
                    textAlign: "right",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSort("margin")}
                >
                  Gross profit{sortArrow("margin")}
                </th>
                <th
                  style={{
                    padding: "6px 8px",
                    textAlign: "right",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSort("marginPct")}
                >
                  Margin %{sortArrow("marginPct")}
                </th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>
                  Markup %
                </th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <tr
                  key={g.key}
                  style={{ borderBottom: "1px solid #f3f4f6" }}
                >
                  <td style={{ padding: "6px 8px", fontWeight: 600 }}>
                    {g.label}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {g.jobs}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {money(g.revenue)}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {money(g.totalCost)}
                  </td>
                  <td
                    style={{
                      padding: "6px 8px",
                      textAlign: "right",
                      color: marginColor(g.margin),
                      fontWeight: 600,
                    }}
                  >
                    {money(g.margin)}
                  </td>
                  <td
                    style={{
                      padding: "6px 8px",
                      textAlign: "right",
                      color: marginColor(g.margin),
                    }}
                  >
                    {pct(g.marginPct)}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {pct(g.markupPct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Cell({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: "11px", color: "#6b7280" }}>{label}</div>
      <div
        style={{
          fontSize: "18px",
          fontWeight: 700,
          color: color || "#374151",
        }}
      >
        {value}
      </div>
    </div>
  );
}
